import {InLayer} from './layer';

/**
 * A burst of paper for a small celebration: `count` pieces falling from the top of whatever holds
 * this, each on its own path and timing, then gone. Pure CSS animation; the numbers are drawn from a
 * fixed sequence so the burst is the same shape every time and never re-rolls on a re-render.
 */
const COLOURS = ['#f75454', '#ffd23f', '#7cc6fe', '#ff8fa3', '#b5e48c'];

/** A deterministic 0..1 sequence — enough spread for confetti, no randomness to re-render on. */
const spread = (index: number, salt: number): number => {
    const x = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;

    return x - Math.floor(x);
};

export const Confetti = ({count = 60}: {count?: number}) => (
    <span className="sd-confetti-burst" aria-hidden="true">
        {Array.from({length: count}, (_unused, index) => (
            <span
                // biome-ignore lint/suspicious/noArrayIndexKey: the pieces are a fixed sequence with no identity but their place
                key={index}
                className="sd-confetti-piece"
                style={
                    {
                        '--x': `${spread(index, 1) * 100}%`,
                        '--delay': `${spread(index, 2) * 0.5}s`,
                        '--duration': `${1.4 + spread(index, 3) * 1.2}s`,
                        '--spin': `${spread(index, 4) * 720 - 360}deg`,
                        '--drift': `${spread(index, 5) * 120 - 60}px`,
                        background: COLOURS[index % COLOURS.length],
                        width: `${6 + Math.round(spread(index, 6) * 6)}px`,
                        height: `${8 + Math.round(spread(index, 7) * 8)}px`,
                    } as React.CSSProperties
                }
            />
        ))}
    </span>
);

/**
 * A burst over the whole window — sidebar and top bar included. Portaled into the overlay layer,
 * because the screens' entrance animation transforms them, and a transformed ancestor would make
 * `position: fixed` cover only the page (see layer.tsx). The wrapper fades itself out and stops
 * taking pointer events (see `.sd-confetti`), so nothing needs a timer to clean up.
 */
export const ConfettiOverlay = () => (
    <InLayer>
        <div className="sd-confetti" aria-hidden="true">
            <Confetti count={90} />
        </div>
    </InLayer>
);
